import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { startCase } from 'lodash-es'
import { useFeatureFlags } from '@node-space/hooks'
import { DateFormat } from 'constants/General'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getLedgerWalletTransactions } from 'services/LedgerService'
import { getWalletTransactions } from 'services/TransactionService'
import {
  FiatTransactionType,
  FiatTransactionTypes,
  LedgerWalletTransaction,
  LedgerWalletTransactionsResponse,
  LegacyTransactionsResponse,
  TransactionSubTypeMapping,
  TransactionTypeMapping,
} from 'types/ledger/ledgerWalletTransactions'
import { Transaction } from 'types/transaction'
import { Paging, WalletType } from 'types/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

const formatTransactionType = (type: string): string => {
  if (!type) return type

  if (TransactionTypeMapping[type]) {
    return TransactionTypeMapping[type]
  }

  if (type === type.toUpperCase()) {
    return startCase(type.toLowerCase())
  }

  return type
}

const formatTransactionSubType = (type: string): string => {
  if (!type) return type

  if (TransactionSubTypeMapping[type]) {
    return TransactionSubTypeMapping[type]
  }

  if (type === type.toUpperCase()) {
    return startCase(type.toLowerCase())
  }

  return type
}

const mapLedgerTransactionsToLegacy = (
  ledgerTransactions: LedgerWalletTransaction[],
  wallet: WalletType
): Transaction[] => {
  return ledgerTransactions?.map(transaction => {
    const isFiatTransaction = Object.values(FiatTransactionTypes).includes(
      transaction?.type as FiatTransactionType
    )

    return {
      amount: transaction?.amount?.value,
      context: {
        id: transaction?.transactionId,
        fromCurrencyCode: transaction?.amount?.currencyCode,
        type: formatTransactionType(transaction?.type),
        ...(!isFiatTransaction && { uuid: transaction?.transactionId }),
        status: transaction?.status,
        ...(!isFiatTransaction && { merchantId: transaction?.paymentId || '' }),
        subType: formatTransactionSubType(transaction?.type),
        toCurrencyCode: !isFiatTransaction
          ? transaction?.beneficiary?.amount?.currencyCode || transaction?.amount?.currencyCode
          : transaction?.amount?.currencyCode,
        ...(!isFiatTransaction &&
          transaction?.beneficiary?.amount?.value && {
            paidActualAmount: String(transaction?.beneficiary?.amount?.value),
          }),
        reference: transaction?.paymentReference,
        paymentReference: transaction?.paymentReference,
        ...(!isFiatTransaction &&
          transaction?.details?.hash && {
            hash: transaction?.details?.hash,
          }),
        ...(!isFiatTransaction && {
          sourceAddress: transaction?.originator?.cryptoAddresses?.addresses || [],
        }),
        exchangeRate: transaction?.details?.exchangeRate,
        paymentMetadata: transaction?.details?.exchangeRate
          ? {
              exchangeRate: transaction?.details?.exchangeRate,
              displayCurrency: transaction?.amount?.currencyCode,
              displayActualAmount: transaction?.amount?.value,
              displayRequiredAmount: transaction?.amount?.value,
            }
          : undefined,
        ...(isFiatTransaction &&
          transaction?.type !== FiatTransactionTypes.PROCESSING_FEE &&
          transaction?.beneficiary && {
            withdrawalDetails: {
              beneficiary: {
                type: transaction?.beneficiary?.entity?.type,
                name: transaction?.beneficiary?.entity?.name,
                bankName: transaction?.beneficiary?.bankAccount?.bankName,
                accountNumber: transaction?.beneficiary?.bankAccount?.accountNumber,
                bankCode: transaction?.beneficiary?.bankAccount?.bankCode,
              },
              valueDate: null,
            },
            amount: String(transaction?.amount?.value || 0),
            externalReference: transaction?.transactionId,
          }),
        sender: transaction?.originator?.entity?.name,
        beneficiary: transaction?.beneficiary?.entity?.name,
      },
      dateCreated: dayjs(transaction?.createdAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM),
      description: transaction?.paymentReference,
      hash: transaction?.transactionId,
      id: transaction?.transactionId,
      type: formatTransactionType(transaction?.type),
      wallet,
      runningBalance: transaction?.runningBalance?.value,
      reference: transaction?.paymentReference,
    }
  })
}

const mapLedgerPaginationToLegacy = (
  ledgerResponse: LedgerWalletTransactionsResponse,
  offset: number
): Paging => ({
  offset,
  total: ledgerResponse?.totalElements,
  max: ledgerResponse?.size,
})

export interface UseGetWalletTransactionsParams {
  wallet: WalletType
  size?: number
  page?: number
  start?: string
  end?: string
  isEnabled?: boolean
}

export const useGetWalletTransactions = ({
  wallet,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  start,
  end,
  isEnabled = true,
}: UseGetWalletTransactionsParams) => {
  const { enableTransactionStore } = useFeatureFlags()

  const { max, offset } = useMemo(() => {
    return {
      max: size,
      offset: page * size,
    }
  }, [size, page])

  const ledgerQuery = useQuery({
    enabled: enableTransactionStore && isEnabled && !!wallet?.lsid,
    queryKey: reactQueryKeys.ledgerWalletTransactions({
      walletReference: wallet?.lsid || '',
      size,
      page,
      ...(start && { start }),
      ...(end && { end }),
    }),
    queryFn: () =>
      getLedgerWalletTransactions({
        walletReference: wallet?.lsid || '',
        size,
        page,
        start,
        end,
      }),
    placeholderData: keepPreviousData,
  })

  const ledgerTransformedData = useMemo<LegacyTransactionsResponse | undefined>(() => {
    if (!ledgerQuery?.data || !wallet) return

    return {
      transactions: mapLedgerTransactionsToLegacy(ledgerQuery?.data?.content || [], wallet),
      paging: mapLedgerPaginationToLegacy(ledgerQuery?.data, offset),
    }
  }, [ledgerQuery?.data, wallet, offset])

  const ledgerQueryResult = useMemo(() => {
    return {
      ...ledgerQuery,
      data: ledgerTransformedData,
    }
  }, [ledgerQuery, ledgerTransformedData])

  const legacyTransactionsQuery = useQuery({
    queryKey: reactQueryKeys.walletTransactions({ max, offset, walletId: wallet?.id }),
    queryFn: () => getWalletTransactions({ max, offset, walletId: wallet?.id }),
    placeholderData: keepPreviousData,
    enabled: !enableTransactionStore && isEnabled && !!wallet?.id,
  })

  return enableTransactionStore ? ledgerQueryResult : legacyTransactionsQuery
}
